import type {
  CheckAssetType,
  CheckBadge,
  CheckBatchStatus,
  CheckCat,
  CheckItem,
  CheckLamp,
  CheckLampCounts,
  CheckPlacement,
  CheckReport,
  CheckReportState,
  CopyField,
  CopyRow,
} from './check';
import { CHECK_STAGE_COUNT, DEFAULT_ETA_SECONDS_PER_ITEM } from './check';

/**
 * 檢查域 wire DTO(snake_case)→ domain mapper(R211;對齊後端確認清單
 * 2026-08-05 C 節報告契約 + H 節 API)。
 * -----------------------------------------------------------------------------
 * 欄名逐條對帳 `creative-test-v2/docs/88-asset-check-api-reference.md`(§8 報告結構 /
 * §2.1 job 形狀)+ BE `app/asset_check/models.py`。wire 變動只改這一檔,UI / store /
 * 元件零改動;測試 `check.mappers.test.ts` 的 fixture **逐字抄 docs/88 的真實範例**,
 * 那組測試就是對帳清單(自己編一個好過的 payload = 綠著讓 production 壞)。
 *
 * 慣例(比照 `job.mappers.ts`):
 *   - **單字母鍵直通**:`s/n/w/u/d/k/t/x/r/g/i` 等 snake==camel,照抄不轉
 *   - **多字鍵轉換**:`verdict_title` → `verdictTitle` 等
 *   - **fail-soft**:缺 optional 欄不炸(BE 分批交付時 FE 不能白屏);必填欄缺 →
 *     `mapCheckReportDto` 回 null,呼叫端當「未檢查」處理
 *   - 未知 lamp 值 → 'unk'(不猜 fail/pass;C 節四燈號封閉集合)
 */

// ─── DTO ────────────────────────────────────────────────────────────────────

export interface CheckBadgeDto {
  s: string;
  n?: number | null;
  w?: number | null;
  u?: number | null;
  /** 檢查時間字串(已格式化 Asia/Taipei)。§0.3 未列必填 → optional,mapper `?? ''`。 */
  d?: string | null;
  reason?: string | null;
  /** 已套用修改建議的則數(BE `CheckBadge.fixed`;08-07 訂版 C-3)。 */
  fixed?: number | null;
}

export interface CheckCatGroupDto {
  n: string;
  counts?: Record<string, number> | null;
  pass: number;
  total: number;
}

export interface CheckCatDto {
  k: string;
  pass: number;
  total: number;
  sev: string;
  counts?: Record<string, number> | null;
  top?: string | null;
  groups?: CheckCatGroupDto[] | null;
  note?: string | null;
}

export interface CheckItemDto {
  /**
   * registry 穩定 id(§8.2:**比對一律用這個,不要比字串**)。
   * R212 補接 —— 之前 mapper 丟掉它,跨報告比對只能拿中文項目名硬碰。
   */
  item_id?: string | null;
  cat: string;
  g?: string | null;
  s: string;
  n: string;
  kind?: string | null;
  r: string;
  fmt?: string | null;
  first?: string | null;
  share?: string | null;
  pos?: string | null;
  ref?: string | null;
  /** 理由(§8.2)。 */
  why?: string | null;
  prompt?: string | null;
  /**
   * 六欄版型第 4 欄:主行 `how` + 副行 `eff`;「檢查項目」欄的位置 chip = `where`。
   * BE 2026-08-06 起 per-item 直給(先前只在 `advice[]`,FE 只能拿中文標題硬碰 →
   * 第 4 欄整欄 fallback 成「已檢查,需要你協助確認。」)。
   */
  how?: string | null;
  where?: string | null;
  eff?: string | null;
}

export interface CheckAdviceDto {
  s: string;
  t: string;
  item_id?: string | null;
  problem: string;
  where: string;
  how: string;
  eff: string;
  cta: string;
}

export interface CheckSummaryEntryDto {
  k: string;
  n: number;
  t: string;
}

export interface CheckRateDto {
  pct: number;
  pass: number;
  total: number;
  label: string;
  hint: string;
  note: string;
}

export interface CheckNextDto {
  note?: string | null;
  main?: string | null;
  second?: string | null;
  /** ⚠️ camel(docs/88 §8.3 範例;R212 訂正 — 原本寫 `second_hint` → 這格恆空字串)。 */
  secondHint?: string | null;
  third?: string | null;
}

export interface CheckFootDto {
  h: string;
  p: string;
}

export interface CheckMergedDto {
  s: string;
  title: string;
  body: string;
  how: string;
  prompt?: string | null;
}

export interface CheckNaDto {
  label: string;
  names: string[];
}

export interface CheckStyleDto {
  t: string;
  n: string;
}

export interface CopyRowDto {
  x: string;
  s: string;
  issue?: string | null;
  why?: string | null;
  fix?: string | null;
  marks?: string[] | null;
}

export interface CopyFieldDto {
  k: string;
  t: string;
  lim: number;
  min: number;
  max: number;
  note?: string | null;
  /** 多行欄位旗標(燈箱兩欄判定;docs/54 assets.13 已請 BE 補此欄)。 */
  ml?: boolean | null;
  rows: CopyRowDto[];
}

/**
 * 版位對照表(docs/88 §8.2 `PlacementSpec`;**文案限定、真的是 wire 欄位** ——
 * BE `_WIRE_FIELDS` 白名單含 `placements`,`build_placements(used_in)` 依
 * `FORMAT_FIELD_BOUNDS` 產出,只列 `usedIn` 裡的格式)。
 *
 * ⚠️ R212 一度把這個 DTO 刪掉、註記「BE 不會給」——**那是錯的**,代價是 per-format
 * 則數上限拿不到:多格式報告的 `fields[].max` 是 BE 取的**跨格式聯集** `max(his)`,
 * 例 usedIn=[RSA, RDA] 時 `short_title.max=15`,而 RDA 的實際 cap 是 5 → 兩個分頁
 * 都放行 15 則,使用者照填、上傳被平台退。`mins` 同理(RSA 的 company_name 下限 0
 * 會被 PMax 的 1 汙染,誤報「至少要 1 則」)。
 */
export interface CheckPlacementDto {
  /** 格式 id(`RESPONSIVE_SEARCH_AD` 等)。 */
  id: string;
  /** 中文顯示名(BE 出;FE 對已知格式仍以自己的 i18n 為準)。 */
  label?: string | null;
  fields?: string[] | null;
  /** per-欄位則數上限。 */
  caps?: Record<string, number> | null;
  /** per-欄位則數下限(0 = 選填)。 */
  mins?: Record<string, number> | null;
}

/**
 * 報告 wire —— ⚠️ **camelCase**(R212 訂正;docs/88 §8.1 + §8.3 真實範例)。
 *
 * R211 預埋時整份當 snake_case 解(`verdict_title`/`pass_names`…),而 BE 的報告回應
 * 是**逐字對齊 D3 `check-data.js` 的 camelCase**(`verdictTitle`/`passNames`…)——
 * 那會讓 `mapCheckReportDto` 每次都在必填閘回 null,報告頁永遠顯示「未檢查」。
 *
 * **混合慣例**(docs/88 §8.1 逐欄核對過,不是筆誤):報告主體 camel,但這四個
 * 保持 snake —— `report_id` / `state` / `applied_texts` / `engine_version` /
 * `spec_version`,以及 `items[].item_id`。**別「順手統一」**,改了就對不上 wire。
 * (Firestore job doc 另有一套:一律 snake — 見 `CheckJobDocDto`。)
 */
export interface CheckReportDto {
  report_id?: string | null;
  id: string;
  name?: string | null;
  type: string;
  typeLabel?: string | null;
  spec?: string | null;
  specSub?: string | null;
  /**
   * 結構化規格(§8.1;圖片/影片)。⚠️ 這四個是 **camelCase**(與 `report_id` /
   * `applied_texts` 那批 snake 例外不同)。有這些就別再 regex 解 `spec` 顯示字串
   * —— `1.91:1` 會被解成 91:1。
   */
  width?: number | null;
  height?: number | null;
  durationSec?: number | null;
  sizeMb?: number | null;
  compo?: string | null;
  srcType?: string | null;
  srcName?: string | null;
  made?: string | null;
  checked?: string | null;
  uploaded?: string | null;
  standard?: string | null;
  sev?: string | null;
  verdictTitle?: string | null;
  verdictLines?: string[] | null;
  verdictDo?: string | null;
  summary?: CheckSummaryEntryDto[] | null;
  cats?: CheckCatDto[] | null;
  items?: CheckItemDto[] | null;
  advice?: CheckAdviceDto[] | null;
  merged?: CheckMergedDto | null;
  na?: CheckNaDto[] | null;
  style?: CheckStyleDto | null;
  rate?: CheckRateDto | null;
  next?: CheckNextDto | null;
  foot?: CheckFootDto[] | null;
  passNames?: string[] | null;
  lowConf?: boolean | null;
  fields?: CopyFieldDto[] | null;
  rowsNote?: string | null;
  groupItems?: string[] | null;
  /** **格式 id 陣列**(RESPONSIVE_SEARCH_AD 等;§8.1)— 不是頓號字串,顯示名 FE 映射。 */
  usedIn?: string[] | null;
  /** 版位 tab 對照表(§8.1/§8.2;只列 `usedIn` 裡的格式)。 */
  placements?: CheckPlacementDto[] | null;
  groupStale?: boolean | null;
  state?: string | null;
  applied_texts?: Record<string, string> | null;
  /** R292.20(BE `dev-update-v21-3`)— 本輪含已套用修正的來源報告 id。 */
  applied_from_report_id?: string | null;
  /** R292.20 — 因素材已變更而失配丟棄的修正條數。 */
  applied_dropped?: number | null;
  engine_version?: string | null;
  spec_version?: string | null;
  /**
   * R424.1(BE docs/137 §5;tag v22-205)— 報告語系(`zh-TW` / `en` / `ja`)。BE `exclude_none`:
   * 舊報告**沒有這欄** = zh-TW。
   */
  locale?: string | null;
}

/**
 * `CheckReportDto` 的鍵名 runtime 清單 —— 給契約鎖比對用(TS 型別在 runtime 不存在)。
 *
 * 下面 `_dtoKeysExhaustive` 讓「DTO 加了欄位但忘了加進這裡」在 **tsc** 就紅,所以這份
 * 清單不會偷偷過期;而 [beContract.test](@/contract/beContract.test.ts) 拿它與 BE 的
 * `_WIRE_FIELDS` 對比,**BE 送了而這裡沒有的欄位會讓測試紅** —— 那正是 08-07~08-08
 * 六個欄位靜默漏接時沒有任何東西會響的缺口。
 */
export const REPORT_DTO_KEYS = [
  'id', 'report_id', 'type', 'typeLabel', 'name', 'srcName', 'srcType', 'made', 'uploaded',
  'checked', 'spec', 'specSub', 'width', 'height', 'durationSec', 'sizeMb', 'standard',
  'sev', 'verdictTitle', 'verdictLines', 'verdictDo', 'summary', 'rate', 'cats', 'items',
  'advice', 'merged', 'na', 'style', 'compo', 'foot', 'next', 'rowsNote', 'fields',
  'passNames', 'lowConf', 'groupItems', 'usedIn', 'placements', 'groupStale', 'state',
  'applied_texts', 'applied_from_report_id', 'applied_dropped',
  'engine_version', 'spec_version', 'locale',
] as const;

/** DTO 加欄但沒補進 `REPORT_DTO_KEYS` → 這一行 tsc 紅。 */
type _MissingDtoKey = Exclude<keyof CheckReportDto, (typeof REPORT_DTO_KEYS)[number]>;
const _dtoKeysExhaustive: _MissingDtoKey extends never ? true : never = true;
void _dtoKeysExhaustive;

/**
 * `POST /asset_check/jobs` 回應 + `GET /asset_check/jobs/{jid}`。
 * `firestore_paths` 欄名比照 workflow jobs mapper — **四鍵全 optional、只硬要求
 * `job`**(asset_check 是 group-scoped root collection,`project` 大概率缺;
 * 後端清單 B 節三輪)。
 */
export interface CheckJobFirestorePathsDto {
  job?: string | null;
  project?: string | null;
  steps_collection?: string | null;
  assets_collection?: string | null;
}

/** 四個子步驟顯示字串(docs/88 §2.1「result.stages」;不當步數常數用 — 分母恆 total×4)。 */
export type CheckJobStagesDto = string[];

/** per-item 狀態沿用 BE `StepStatus`(後端清單 B 節拍板,省 FE 兩套映射)。 */
export type CheckJobItemStatusDto =
  | 'WAITING'
  | 'PENDING'
  | 'RUNNING'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'CANCELLED';

/** docs/88 §2.1「result.items[]」逐欄(snake_case;Firestore items 子集合 doc 同形)。 */
export interface CheckJobItemDto {
  item_key?: string | null;
  lib_id: string;
  item_id: string;
  seq?: number | null;
  asset_kind?: string | null;
  display_name?: string | null;
  status: CheckJobItemStatusDto;
  /** 該件的子步驟 0–3(§2.1;R211 誤寫成 `step` → 進度子步驟恆 0)。 */
  stage?: number | null;
  badge?: CheckBadgeDto | null;
  /** SUCCEEDED 後才有 — 拿去查報告。 */
  report_id?: string | null;
  /** FAILED 時的原因(= badge.reason)。 */
  error_reason?: string | null;
  /** 對帳三態;§2.1 明示 FE 可忽略。 */
  settlement?: string | null;
  attempt?: number | null;
}

/** docs/88 §2.1「result.job」逐欄(snake_case)。 */
export interface CheckJobDto {
  job_id: string;
  /** BE `JobStatus`(PENDING/RUNNING/PAUSED/SUCCEEDED/FAILED/CANCELLED)。 */
  status: string;
  total?: number | null;
  done?: number | null;
  failed?: number | null;
  cancelled?: number | null;
  /** **已完成件**的燈號累計(完成 toast 分桶用)。 */
  counts?: Record<string, number> | null;
  /** 送檢順序的 item_key 陣列 = 判斷「檢查中/等待中」的依據。 */
  order?: string[] | null;
  current_item_key?: string | null;
  /** 目前子步驟 0–3;每件收斂時歸零。 */
  current_stage?: number | null;
  reserved_credits?: number | null;
  settled_credits?: number | null;
  refunded_credits?: number | null;
  /** 單件預估秒數(倒數用;預設 33)。R211 硬編 0.55 分/素材 → 改吃這欄。 */
  eta_seconds_per_item?: number | null;
  engine_version?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  finished_at?: string | null;
}

/**
 * ⚠️ **REST 回應是兩層**:`result.job` / `result.items` / `result.firestore_paths` /
 * `result.stages`(docs/88 §2.1;`POST /jobs`、`GET /jobs/{id}`、cancel 三條同形)。
 * R211 預埋把 job 欄位平鋪在 `result` 頂層 → `job_id` 讀不到、mapper 恆回 null
 * (扣了點卻完全看不到進度)。
 *
 * **Firestore job doc 則是平鋪的**(§2.3)→ listener 走 `CheckJobDocDto`,別混用。
 */
export interface CheckJobResponseDto {
  job: CheckJobDto;
  items?: CheckJobItemDto[] | null;
  firestore_paths?: CheckJobFirestorePathsDto | null;
  stages?: CheckJobStagesDto | null;
  /** cancel 專屬:本次取消掉幾件(§3)。 */
  cancelled?: number | null;
}

/** Firestore **job doc**(§2.3):平鋪 snake_case、**沒有 items**(items 在子集合)。 */
export type CheckJobDocDto = CheckJobDto;

// ─── 值域 normalize ─────────────────────────────────────────────────────────

const LAMPS = new Set<CheckLamp>(['fail', 'warn', 'pass', 'unk']);

/** 未知燈號一律 'unk'(不猜 fail/pass — 猜錯會誤導使用者是否能刊出)。 */
function lamp(v: string | null | undefined): CheckLamp {
  return LAMPS.has(v as CheckLamp) ? (v as CheckLamp) : 'unk';
}

const CAT_KEYS = new Set(['G', 'A', 'B', 'C', 'D']);

function counts(
  raw: Record<string, number> | null | undefined,
): Partial<Record<'fail' | 'warn' | 'unk', number>> {
  const out: Partial<Record<'fail' | 'warn' | 'unk', number>> = {};
  if (!raw) return out;
  for (const k of ['fail', 'warn', 'unk'] as const) {
    if (typeof raw[k] === 'number') out[k] = raw[k];
  }
  return out;
}

// ─── badge ──────────────────────────────────────────────────────────────────

/**
 * badge mapper。`s` 值域 = 四燈號 + `err`;**未知值回 null**(呼叫端當「未檢查」—
 * 顯示層的 none 態是缺席語意,不是第六個 s 值)。
 */
export function mapCheckBadgeDto(dto: CheckBadgeDto | null | undefined): CheckBadge | null {
  if (!dto || typeof dto.s !== 'string') return null;
  const s = dto.s === 'err' ? 'err' : LAMPS.has(dto.s as CheckLamp) ? (dto.s as CheckLamp) : null;
  if (!s) return null;
  return {
    s,
    ...(typeof dto.n === 'number' ? { n: dto.n } : {}),
    ...(typeof dto.w === 'number' ? { w: dto.w } : {}),
    ...(typeof dto.u === 'number' ? { u: dto.u } : {}),
    d: dto.d ?? '',
    ...(dto.reason ? { reason: dto.reason } : {}),
    // 訂版 C-3:已套用則數。BE 已把 badge 扣抵算好,這欄只供 hover 組句
    ...(typeof dto.fixed === 'number' ? { fixed: dto.fixed } : {}),
  };
}

// ─── report ─────────────────────────────────────────────────────────────────

const ASSET_TYPES = new Set<CheckAssetType>(['video', 'image', 'text']);
const REPORT_STATES = new Set<CheckReportState>(['ok', 'stale', 'lowconf']);

function mapCats(raw: CheckCatDto[] | null | undefined): CheckCat[] {
  return (raw ?? [])
    .filter((c) => CAT_KEYS.has(c.k))
    .map((c) => ({
      k: c.k as CheckCat['k'],
      pass: c.pass ?? 0,
      total: c.total ?? 0,
      sev: lamp(c.sev),
      counts: counts(c.counts),
      top: c.top ?? '',
      ...(c.groups?.length
        ? {
            groups: c.groups.map((g) => ({
              n: g.n,
              counts: counts(g.counts),
              pass: g.pass ?? 0,
              total: g.total ?? 0,
            })),
          }
        : {}),
      ...(c.note ? { note: c.note } : {}),
    }));
}

function mapItems(raw: CheckItemDto[] | null | undefined): CheckItem[] {
  return (raw ?? [])
    .filter((it) => CAT_KEYS.has(it.cat))
    .map((it) => {
      const s = lamp(it.s);
      return {
        cat: it.cat as CheckItem['cat'],
        s,
        n: it.n,
        r: it.r,
        ...(it.item_id ? { itemId: it.item_id } : {}),
        ...(it.why ? { why: it.why } : {}),
        ...(it.g ? { g: it.g } : {}),
        ...(it.kind ? { kind: it.kind } : {}),
        ...(it.fmt ? { fmt: it.fmt } : {}),
        ...(it.first ? { first: it.first } : {}),
        ...(it.share ? { share: it.share } : {}),
        ...(it.pos ? { pos: it.pos } : {}),
        ...(it.ref ? { ref: it.ref } : {}),
        // C:116 契約 = pass/unk 不給 prompt;BE 若誤給,FE 端剝除(顯示層才不會出現
        // 「這項通過了但這裡有修正 Prompt」的自相矛盾)
        ...(it.prompt && s !== 'pass' && s !== 'unk' ? { prompt: it.prompt } : {}),
        // 六欄版型第 4 欄(how 主行 / eff 副行)與位置 chip(where)—— per-item 直給,
        // 不必再拿中文標題去 advice[] 硬碰(§8.2:比對一律用 item_id)
        ...(it.how ? { how: it.how } : {}),
        ...(it.where ? { where: it.where } : {}),
        ...(it.eff ? { eff: it.eff } : {}),
      };
    });
}

function mapCopyFields(raw: CopyFieldDto[] | null | undefined): CopyField[] | undefined {
  if (!raw?.length) return undefined;
  return raw.map((f) => ({
    k: f.k,
    t: f.t,
    lim: f.lim ?? 0,
    min: f.min ?? 0,
    max: f.max ?? 0,
    ...(f.note ? { note: f.note } : {}),
    ...(typeof f.ml === 'boolean' ? { ml: f.ml } : {}),
    rows: (f.rows ?? []).map((row): CopyRow => {
      // 畫記字串必須是原文的子字串(字面比對前提);BE 給了對不上的一律丟掉,
      // 否則畫記段落錯位(markSegments 以 indexOf 掃描)
      const marks = (row.marks ?? []).filter((m) => !!m && row.x.includes(m));
      return {
        x: row.x,
        s: lamp(row.s),
        ...(row.issue ? { issue: row.issue } : {}),
        ...(row.why ? { why: row.why } : {}),
        ...(row.fix ? { fix: row.fix } : {}),
        ...(marks.length ? { marks } : {}),
      };
    }),
  }));
}

/**
 * `placements[]` → domain(`id→k` / `label→t` / `caps→cap` / `mins→min`)。
 *
 * 缺 `id` 的筆丟掉(分頁 key 認不出來,留著只會是一個點不開的空 tab);`label` 缺
 * 就給空字串 —— 顯示層對已知格式本來就用自己的 i18n,BE label 只是新格式的墊底。
 */
function mapPlacements(
  raw: CheckPlacementDto[] | null | undefined,
): CheckPlacement[] | undefined {
  const out = (raw ?? [])
    .filter((p) => !!p?.id)
    .map(
      (p): CheckPlacement => ({
        k: p.id,
        t: p.label ?? '',
        fields: p.fields ?? [],
        cap: p.caps ?? {},
        ...(p.mins ? { min: p.mins } : {}),
      }),
    );
  return out.length ? out : undefined;
}

/**
 * 報告 mapper(R212 訂正:wire 是 camelCase — 見 `CheckReportDto` 檔頭)。
 *
 * **必填閘收窄到 `id` + 合法 `type`**:R211 版還要求 `name`/`verdictTitle` 非空,
 * 但那兩欄在 wire 上是「後端確定性組句」的產物,一旦某型報告少給就整份回 null =
 * 報告頁謊報「未檢查」。缺字串一律 fail-soft 成 `''`(頁面自己有空態),**唯獨
 * 認不出 type 才回 null** —— 那時連版型都選不出來,渲染出來的東西沒有意義。
 */
export function mapCheckReportDto(dto: CheckReportDto | null | undefined): CheckReport | null {
  if (!dto || !dto.id || !ASSET_TYPES.has(dto.type as CheckAssetType)) return null;

  const fields = mapCopyFields(dto.fields);
  const placements = mapPlacements(dto.placements);
  return {
    id: dto.id,
    name: dto.name ?? '',
    type: dto.type as CheckAssetType,
    spec: dto.spec ?? '',
    srcType: dto.srcType ?? '',
    srcName: dto.srcName ?? '',
    made: dto.made ?? '',
    checked: dto.checked ?? '',
    uploaded: dto.uploaded ?? null,
    standard: dto.standard ?? '',
    sev: lamp(dto.sev),
    verdictTitle: dto.verdictTitle ?? '',
    verdictLines: dto.verdictLines ?? [],
    summary: (dto.summary ?? []).map((s) => ({ k: lamp(s.k), n: s.n ?? 0, t: s.t ?? '' })),
    cats: mapCats(dto.cats),
    items: mapItems(dto.items),
    advice: (dto.advice ?? []).map(({ item_id, ...a }) => ({
      ...a,
      s: lamp(a.s),
      itemId: item_id ?? undefined,
    })),
    foot: dto.foot ?? [],
    passNames: dto.passNames ?? [],
    next: dto.next
      ? {
          note: dto.next.note ?? '',
          main: dto.next.main ?? '',
          second: dto.next.second ?? '',
          secondHint: dto.next.secondHint ?? '',
          third: dto.next.third ?? '',
        }
      : { note: '', main: '', second: '', secondHint: '', third: '' },
    ...(dto.report_id ? { reportId: dto.report_id } : {}),
    ...(dto.typeLabel ? { typeLabel: dto.typeLabel } : {}),
    ...(dto.specSub ? { specSub: dto.specSub } : {}),
    // 結構化規格:**用型別判定不用 truthy** —— 0 是合法值(例 sizeMb 四捨五入後
    // 的 0.0、regex 解不出來時 BE 給 null 才是「沒有」)
    ...(typeof dto.width === 'number' ? { width: dto.width } : {}),
    ...(typeof dto.height === 'number' ? { height: dto.height } : {}),
    ...(typeof dto.durationSec === 'number' ? { durationSec: dto.durationSec } : {}),
    ...(typeof dto.sizeMb === 'number' ? { sizeMb: dto.sizeMb } : {}),
    ...(dto.compo ? { compo: dto.compo } : {}),
    ...(dto.verdictDo ? { verdictDo: dto.verdictDo } : {}),
    ...(dto.merged ? { merged: { ...dto.merged, s: lamp(dto.merged.s), prompt: dto.merged.prompt ?? undefined } } : {}),
    ...(dto.na?.length ? { na: dto.na } : {}),
    ...(dto.style ? { style: dto.style } : {}),
    ...(dto.rate ? { rate: dto.rate } : {}),
    ...(fields ? { fields } : {}),
    ...(dto.rowsNote ? { rowsNote: dto.rowsNote } : {}),
    ...(dto.groupItems?.length ? { groupItems: dto.groupItems } : {}),
    ...(dto.usedIn?.length ? { usedIn: dto.usedIn } : {}),
    // per-format 則數上/下限的唯一真理源(缺席才由 FE 本機表依 usedIn 派生)
    ...(placements ? { placements } : {}),
    ...(dto.lowConf ? { lowConf: true } : {}),
    // groupStale 是 **wire 欄位**(§8.1)— 之前 FE 自算「applied 非空」→ 重載後
    // 出現幽靈橫幅,按下去必 409 CHECK_GROUP_ITEMS_NOT_STALE。
    ...(dto.groupStale ? { groupStale: true } : {}),
    ...(REPORT_STATES.has(dto.state as CheckReportState)
      ? { state: dto.state as CheckReportState }
      : {}),
    ...(dto.applied_texts && Object.keys(dto.applied_texts).length
      ? { appliedTexts: dto.applied_texts }
      : {}),
    // R292.20 — 複檢帶入套用稿的兩個顯示欄(BE `dev-update-v21-3`;缺席 = 舊 BE)
    ...(typeof dto.applied_from_report_id === 'string' && dto.applied_from_report_id
      ? { appliedFromReportId: dto.applied_from_report_id }
      : {}),
    ...(typeof dto.applied_dropped === 'number' && dto.applied_dropped > 0
      ? { appliedDropped: dto.applied_dropped }
      : {}),
    ...(dto.engine_version ? { engineVersion: dto.engine_version } : {}),
    ...(dto.spec_version ? { specVersion: dto.spec_version } : {}),
    // R424.1 — 缺欄 = 舊報告 = zh-TW(BE 契約);**不過濾值域**(R296:wire 值域開放,認不得的
    // 語系照原碼顯示,不可以把它說成繁中)。
    locale: typeof dto.locale === 'string' && dto.locale.trim() ? dto.locale : 'zh-TW',
  };
}

// ─── batch status ───────────────────────────────────────────────────────────

/** wire `JobStatus` / per-item `StepStatus` → 顯示態(FE 只在此一處映射)。 */
function batchState(status: string, failed: number): CheckBatchStatus['state'] {
  switch (status) {
    case 'SUCCEEDED':
      return failed > 0 ? 'fail' : 'done';
    case 'FAILED':
      return 'fail';
    case 'CANCELLED':
      return 'cancelled';
    default:
      // PENDING / RUNNING / PAUSED 皆視為進行中(PAUSED 也還在批次生命週期內)
      return 'run';
  }
}

/** item_key 一律以 wire 的 `item_key` 為準,缺才自己拼(§2.1:order 用同一組字串)。 */
function itemKeyOf(it: CheckJobItemDto): string {
  return it.item_key || `${it.lib_id}:${it.item_id}`;
}

/**
 * 子步驟索引上界。**BE 已保證恆 4 階**(待答清單 B-6,2026-08-07:「`stages` 恆 4
 * (後端常數 tuple),`current_stage` 0–3 就是索引」)—— 所以這個 3 是**契約值**不是猜的。
 *
 * 階數的單一來源是 `check.ts` 的 `CHECK_STAGE_COUNT`(百分比公式的分母同源);
 * 契約鎖 `asset_check.progress_stage_count` 會在 BE 改階數時變紅。剩下要人工跟的
 * 只有 `CheckProgressBar` 的四個 i18n key(多一階 = 多一句翻譯,無法自動生)。
 */
const STAGE_MAX = CHECK_STAGE_COUNT - 1;

/** 0–3 夾取(超界當 0,不讓進度條爆框)。 */
function stageOf(v: number | null | undefined): CheckBatchStatus['step'] {
  return (typeof v === 'number' && v >= 0 && v <= STAGE_MAX ? v : 0) as CheckBatchStatus['step'];
}

/** `job.counts` → 四桶(缺值全 0;完成 toast 分桶用)。 */
function lampCounts(raw: Record<string, number> | null | undefined): CheckLampCounts {
  return {
    fail: raw?.fail ?? 0,
    warn: raw?.warn ?? 0,
    unk: raw?.unk ?? 0,
    pass: raw?.pass ?? 0,
  };
}

/** items[] → per-key badge(**只收終局件**)+ report_id 對照表。 */
function mapJobItems(items: CheckJobItemDto[]): {
  results: Record<string, CheckBadge>;
  reportIds: Record<string, string>;
} {
  const results: Record<string, CheckBadge> = {};
  const reportIds: Record<string, string> = {};
  for (const it of items) {
    const k = itemKeyOf(it);
    // 只有終局(SUCCEEDED/FAILED)且有 badge 才算「已寫回」— RUNNING 中的 badge
    // 會讓清單提早停掉 spinner(checkRowLiveState 以 results 有無判 live 態)。
    // §2.1 也明示 badge「SUCCEEDED / FAILED 後才有」。
    const badge = mapCheckBadgeDto(it.badge);
    if (badge && (it.status === 'SUCCEEDED' || it.status === 'FAILED')) results[k] = badge;
    if (it.report_id) reportIds[k] = it.report_id;
  }
  return { results, reportIds };
}

/**
 * REST job 回應(**兩層**:`{job, items, firestore_paths, stages}`)→ `CheckBatchStatus`。
 * `POST /jobs`、`GET /jobs/{id}`、cancel 三條同形(docs/88 §2.1)。
 */
export function mapCheckJobStatusDto(
  dto: CheckJobResponseDto | null | undefined,
): CheckBatchStatus | null {
  if (!dto?.job?.job_id) return null;
  const job = dto.job;
  const items = dto.items ?? [];
  const { results, reportIds } = mapJobItems(items);

  // 進行中那件:優先信 wire 的 current_item_key(§2.1),缺才從 items 找 RUNNING。
  const running = items.find((it) => it.status === 'RUNNING') ?? null;
  const currentKey = job.current_item_key ?? (running ? itemKeyOf(running) : null);
  // 子步驟同理:job.current_stage 優先,退 items[].stage。
  const step = stageOf(job.current_stage ?? running?.stage);
  const failed = job.failed ?? items.filter((it) => it.status === 'FAILED').length;

  return {
    batchId: job.job_id,
    state: batchState(job.status, failed),
    total: job.total ?? items.length,
    // done = **成功件**(§2.1;與 failed 分開計 — 不可相減)
    done: job.done ?? Object.keys(results).length,
    failed,
    cancelled: job.cancelled ?? 0,
    counts: lampCounts(job.counts),
    step,
    currentKey,
    // order 以 wire 為準(§2.1「送檢順序」= 檢查中/等待中的判準);缺才用 items 順序
    order: job.order?.length ? job.order.slice() : items.map(itemKeyOf),
    results,
    reportIds,
    etaSecondsPerItem: job.eta_seconds_per_item ?? DEFAULT_ETA_SECONDS_PER_ITEM,
    // BE 給 created_at 就用它(reload 後 ETA/TTL 才不會從 0 重算);缺就用現在
    startedAt: job.created_at ? new Date(job.created_at).getTime() || Date.now() : Date.now(),
    ...(dto.stages?.length ? { stages: dto.stages.slice() } : {}),
    ...(dto.firestore_paths?.job ? { firestorePath: dto.firestore_paths.job } : {}),
    ...(dto.firestore_paths?.steps_collection
      ? { itemsPath: dto.firestore_paths.steps_collection }
      : {}),
  };
}

/**
 * Firestore **job doc**(平鋪 snake_case、**無 items**)→ 部分更新。
 *
 * ⚠️ 與 REST 兩層形狀不同(§2.3)。listener 拿到的 job doc 沒有 items,所以這支
 * **只回計數/進度欄**,`results`/`order` 由 items 子集合那一路(`mapCheckJobItemsDocs`)
 * 補 —— R211 用同一支 mapper 吃兩種來源,結果每次 job doc 推播都把 results 清空。
 */
export function mapCheckJobDoc(
  doc: CheckJobDocDto | null | undefined,
): Partial<CheckBatchStatus> | null {
  if (!doc?.status) return null;
  const failed = doc.failed ?? 0;
  return {
    state: batchState(doc.status, failed),
    ...(typeof doc.total === 'number' ? { total: doc.total } : {}),
    ...(typeof doc.done === 'number' ? { done: doc.done } : {}),
    failed,
    cancelled: doc.cancelled ?? 0,
    counts: lampCounts(doc.counts),
    step: stageOf(doc.current_stage),
    currentKey: doc.current_item_key ?? null,
    ...(doc.order?.length ? { order: doc.order.slice() } : {}),
    ...(typeof doc.eta_seconds_per_item === 'number'
      ? { etaSecondsPerItem: doc.eta_seconds_per_item }
      : {}),
  };
}

/** Firestore **items 子集合** docs → per-key badge / report_id(同 REST items 形狀)。 */
export function mapCheckJobItemsDocs(docs: Array<CheckJobItemDto | null | undefined>): {
  results: Record<string, CheckBadge>;
  reportIds: Record<string, string>;
  order: string[];
} {
  const valid = docs.filter((d): d is CheckJobItemDto => !!d?.lib_id && !!d?.item_id);
  const sorted = valid.slice().sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  const { results, reportIds } = mapJobItems(sorted);
  return { results, reportIds, order: sorted.map(itemKeyOf) };
}
